import React, { useRef, useCallback, useState } from 'react';
import noop from 'lodash/noop';
import FinalForm, { Field, type TFinalFormRenderProp } from '@framework/ui/Organisms/FinalForm';
import Textarea from '@creditflowresearch/uilib/Textarea';
import UploadFile from '@/creditflowresearch/ui/common/components/UploadFile';
import FinsightModal from '@/creditflowresearch/ui/common/components/FinsightModal';
import ReCaptcha from '@framework/ui/Molecules/Form/ReCaptcha';
import Button from '@creditflowresearch/uilib/Button';
import { usePortfoliosContext } from '@/creditflowresearch/application/PortfolioMonitor/PortfoliosContext';
import validate from './validate';
import { UNKNOWN_BOND_MODAL, UNKNOWN_BOND_MODAL_FORM, SUBMIT_UNKNOWN_BOND_MODAL_FORM } from './constants';

import styles from './styles.module.scss';
import spacesStyles from '@creditflowresearch/uilib/styles/helpers/spaces.module.scss';

const UnknownBondModal = () => {
  const {
    unknownBond,
    setUnknownBond,
    submitUnknownBond,
  } = usePortfoliosContext();
  const inputUploadFile = useRef(null);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  let resetFinalFormState = noop;

  const onCloseClicked = useCallback(
    () => {
      setUnknownBond(null);
      inputUploadFile.current.deleteFile();
      resetFinalFormState();
    },
    [resetFinalFormState],
  );

  const onSubmit = async (values) => {
    setIsFetching(true);
    await submitUnknownBond({
      cusip: unknownBond?.cusip,
      bondName: unknownBond?.bond?.customBondName || '',
      comment: values.comments || '',
      file: inputUploadFile.current.getFile(),
      token: values?.recaptcha,
    });
    setIsFetching(false);
    onCloseClicked();
  };

  const renderFields = useCallback<TFinalFormRenderProp>(
    ({
      valid,
      form,
    }) => {
      resetFinalFormState = form.restart;
      return (
        <>
          <div className={ styles.headerText }>
            <h1>Don’t see your Bond?</h1>
            <p className={ styles.modalDescription }>
              Report a missing deal or ask us a question!
            </p>
          </div>
          <Field
            textareaClassName={ styles.textarea }
            placeholder="Please provide additional details..."
            label="COMMENT"
            name="comments"
            component={ Textarea }
            dataTest="doNotSeeBondModalFormTextArea"
          />
          <UploadFile
            formName={ UNKNOWN_BOND_MODAL_FORM }
            ref={ inputUploadFile }
          />
          <div className={ spacesStyles.mbxl }>
            <Field
              name="recaptcha"
              component={ ReCaptcha }
            />
          </div>
          <Button
            dataTest={ SUBMIT_UNKNOWN_BOND_MODAL_FORM }
            type="submit"
            disabled={ !valid || isFetching }
            variant="solid"
            color="primary"
            className={ styles.submitButton }
          >
            Submit
          </Button>
        </>
      );
    },
    [resetFinalFormState],
  );

  return (
    unknownBond && (
      <FinsightModal
        hideOnOverlayClicked
        successText="Your feedback has been submitted."
        modalTitle={ <>CreditFlow<br />Portfolio Monitor</> }
        onCloseClicked={ onCloseClicked }
        isVisible={ !!unknownBond }
        isFetching={ false }
        dataTest={ UNKNOWN_BOND_MODAL }
      >
        <FinalForm
          dataTest={ UNKNOWN_BOND_MODAL_FORM }
          onSubmit={ onSubmit }
          render={ renderFields }
          validate={ validate }
        />
      </FinsightModal>
    )
  );
};

export default UnknownBondModal;
